import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// API/Components/Resources
import axiosInstance from '../components/axios';
import DataLoading from '../components/data_loading';
import { AnalysisResultToStateColor } from './enums';
// import icon from '../images/map_markers/marker_r.png';

// Material UI
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';

// React-Leaflet
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Box, Grid } from '@mui/material';

const redIcon = L.icon({
	iconUrl: '../images/map_markers/marker_r.svg',
	shadowUrl: iconShadow,
	iconSize: [27, 43],
	popupAnchor: [0, -40],
	iconAnchor: [13, 43],
});

const yellowIcon = L.icon({
	iconUrl: '../images/map_markers/marker_y.svg',
	shadowUrl: iconShadow,
	iconSize: [27, 43],
	popupAnchor: [0, -40],
	iconAnchor: [13, 43],
});

const greenIcon = L.icon({
	iconUrl: '../images/map_markers/marker_g.svg',
	shadowUrl: iconShadow,
	iconSize: [27, 43],
	popupAnchor: [0, -40],
	iconAnchor: [13, 43],
});

const MapStyle = styled('div')({
	height: '85vh',
	width: '100%',
	'& .leaflet-container': {
		height: '100%',
	},
});

const ButtonStyle = styled(Button)({
	backgroundColor: "#B1282E",
	color: "#FFFFFF",
	"&:hover": {
		backgroundColor: "#FFFFFF",
		color: "#B1282E",
	},
});

// Set the maximum Latitude and Longitude difference for auto-grouping
const DIFF_LAT_LON = [0.0001, 0.0001];

function groupLocationsByPosition(locationArray) {

	const result = [];
	
	locationArray.forEach((location) => {
		let groupKey = null;
		
		for (const key in result) {
			const groupLocation = result[key][0];
			const diffLat = Math.abs(location.latitude - groupLocation.latitude);
			const diffLon = Math.abs(location.longitude - groupLocation.longitude);
			if (diffLat <= DIFF_LAT_LON[0] && diffLon <= DIFF_LAT_LON[1]) {
				groupKey = key;
				break;
			}
		}
		if (groupKey) {
			result[groupKey].push(location);
			result[groupKey].count++;

			if (location.system_analysis > result[groupKey].analysis) {
				result[groupKey].analysis = location.system_analysis;
			}
		} else {
			const key = `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
			(result[key] || (result[key] = [])).push(location);
			result[key].count = 1;
			result[key].analysis = location.system_analysis;
		}
	});

	return result;
}

function createPopup(locationGroup) {

	const popupLines = locationGroup.map((location) => {
		return (
			<Grid
				container
				spacing={0}
				key={location.location_id}
				alignItems="center"
				sx={{
					// width: '100%',
					// maxWidth: '300px',
					backgroundColor: AnalysisResultToStateColor(location.system_analysis) + "aa",
					marginY: '2px',
					paddingX: 1,
				}}
			>
				<Grid item xs={6}>
					<Typography
						variant="body1"
						fontFamily='Saira, sans-serif'
					>
						<strong>{location.location_name}</strong>
					</Typography>
				</Grid>
				<Grid item xs={4}>
					<Typography
						variant="body1"
						fontFamily='Saira, sans-serif'
						align='center'
					>
						<strong>SN: </strong>{location.serial_number}
					</Typography>
				</Grid>
				<Grid item xs={2}>
					<Grid
						component={RouterLink}
						to={`/location/${location.location_id}`}
					>
						<ButtonStyle variant="outlined" size="small" sx={{ width: '30px', backgroundColor: 'black', px: 0 }}>
							<LinkIcon />
						</ButtonStyle>
					</Grid>
				</Grid>
			</Grid>
		);
	})

	return (
		<Popup
			minWidth={400}
			maxHeight={500}
		>
			<Typography
				variant="h6"
				fontFamily='Saira, sans-serif'
				align='center'
			>
				Locations here:
			</Typography>
			{popupLines}
		</Popup>
	);
}

function createMarkers(groupedLocations) {

	const markers = Object.keys(groupedLocations).map((key) => {
		const group = groupedLocations[key];
		const popup = createPopup(group, key);
		
		// Set a map marker icon based on the highest analysis result in the group		
		let icon = redIcon;
		if (group.analysis === 1) {
			icon = greenIcon;
		} else if (group.analysis === 2 || group.analysis === 3) {
			icon = yellowIcon;
		} else {
			icon = redIcon;
		}

		return (
			<Marker
				position={[group[0].latitude, group[0].longitude]}
				key={key}
				icon={icon}
			>
				{popup}
			</Marker>
		);
	});
	
	return markers;
}

function LocationMap(props) {
	
	const { locations } = props;
	
	const groupedLocations = groupLocationsByPosition(locations);
	let markers = createMarkers(groupedLocations);

	// set the center of the map to 39.8283, -98.5795 by default, then check for locations and choose the first's latlong for centering
	let center = [39.8283, -98.5795];  // Geographic center of the contiguous United States
	const firstKey = Object.keys(groupedLocations)[0];  // Get first latlong key
	if (typeof firstKey !== 'undefined') {
		const firstLoc = groupedLocations[firstKey][0];  // Get first location of that key
		center = [firstLoc.latitude, firstLoc.longitude];
	}

	return (
		<MapStyle>
			<MapContainer center={center} zoom={4} scrollWheelZoom={false}>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				{markers}
			</MapContainer>
		</MapStyle>
	);
}

export default function LoadLocationsMap() {

	const MapLoading = DataLoading(LocationMap);
	const [locationState, setLocationState] = useState({
		loading: true,
		locations: null,
	});

	useEffect(() => {
		axiosInstance.get('/map/').then(res => {
			const locData = res.data.locations;
			setLocationState({
				loading: false,
				locations: locData ? locData : [],
			})
		});
	}, [setLocationState]);

	return (
		<MapLoading
			isLoading={locationState.loading}
			dataType="map"
			locations={locationState.locations}
		/>
	);
}